// ── Touch Input Provider ──
// Orchestrates TouchLookController, TouchJoystick, TouchButtons (and optionally GyroAim)
// into a unified InputProvider that emits the same InputState the game already uses.

import type { InputState } from '../core/input';
import type { InputMode } from '../core/device';
import type { InputProvider } from './input-provider';
import { TouchLookController } from './touch-look';
import { TouchJoystick } from './touch-joystick';
import { TouchButtons } from './touch-buttons';
import { GyroAimController } from './gyro-aim';
import { GrenadeStrengthSelector, type GrenadeStrength } from './grenade-strength';
import { settingsStore, type ControlMode } from '../core/settings';
import { voiceChat } from '../audio/voice';

export class TouchInputProvider implements InputProvider {
  readonly mode: InputMode;

  private look: TouchLookController | null = null;
  private joystick: TouchJoystick | null = null;
  private buttons: TouchButtons | null = null;
  private gyro: GyroAimController | null = null;

  private useGyro: boolean;

  // H1: Weapon switch rising-edge detection
  private prevWeaponSwitch = false;
  private weaponSwitchCallback: (() => void) | null = null;

  // C6: Mobile push-to-talk rising/falling edge -> voiceChat start/stop.
  private prevPTT = false;

  // MOB-C2: Mobile scope toggle rising-edge detection (analog of toggleScope on desktop).
  private prevScope = false;
  private scopeCallback: (() => void) | null = null;

  // MOB-C3: Mobile interact (plant/defuse/use) rising-edge detection (analog of E on desktop).
  private prevInteract = false;
  private interactCallback: (() => void) | null = null;

  // F-MOB-H3: Mobile buy/scoreboard/grenade/ping rising-edge detection.
  private prevBuy = false;
  private buyCallback: (() => void) | null = null;
  private prevScoreboard = false;
  private scoreboardCallback: (() => void) | null = null;
  private prevGrenade = false;
  private grenadeCycleCallback: (() => void) | null = null;
  private prevPing = false;
  private pingCallback: (() => void) | null = null;
  // F-MOB-H3 (chat): chat-button rising-edge — opens the ChatComposer overlay.
  private prevChat = false;
  private chatCallback: (() => void) | null = null;

  // MOB-W6: three rising-edge slots for the customizable quick-buy buttons.
  // Callback receives the slot index (0-2) so a single handler can dispatch
  // to QuickBuyController.triggerBuy(slot).
  private prevQuickBuy: [boolean, boolean, boolean] = [false, false, false];
  private quickBuyCallback: ((slot: 0 | 1 | 2) => void) | null = null;

  // F-GP-006: latched rising edges for inspect / drop. The encode call site in
  // game.ts pulls the latched flag once per tick via popInspect/popDrop and
  // ORs it into the actions bitfield, mirroring how desktop reads
  // ACT_INSPECT / ACT_DROP from the keybind state.
  private prevInspect = false;
  private prevDrop = false;
  private pendingInspect = false;
  private pendingDrop = false;

  // BUG-MT-1 / BUG-W-10: latched rising edge for the mobile USE (interact) button.
  // The existing `interactCallback` only fires a one-shot JSON 'use' message via
  // onInteract(); the binary input frame never had ACT_USE set on touch because
  // game.ts had no per-tick latch to read. popInteract() mirrors popInspect /
  // popDrop so game.ts can OR the press into the actions bitfield at encode-time.
  private pendingInteract = false;

  // MOB-W4: Grenade throw uses the fire button on mobile (no separate throw button).
  // Vertical drag while fire is held maps to throw strength via GrenadeStrengthSelector.
  // Throw fires on the falling edge of fire (release-to-throw, charge-while-held).
  private prevFire = false;
  private grenadeFireCallback: ((strength: GrenadeStrength) => void) | null = null;
  // BUG-MT-21: optional predicate gate. When set, the grenadeFireCallback is
  // only invoked on fire-release when the predicate returns true. Lets the
  // host (game.ts) refuse the callback at the source when no grenade is
  // selected, rather than relying solely on a host-side check after the call.
  private grenadeFireGate: (() => boolean) | null = null;
  private grenadeStrength = new GrenadeStrengthSelector();
  private fireBtnEl: HTMLElement | null = null;
  private _fireTouchYStart = new Map<number, number>();
  private _onFireTouchStart: ((e: TouchEvent) => void) | null = null;
  private _onFireTouchMove: ((e: TouchEvent) => void) | null = null;
  private _onFireTouchEnd: ((e: TouchEvent) => void) | null = null;

  // M1: Exposed settings — aim assist strength and control mode
  private _aimAssistStrength = 0.7;
  private _controlMode: ControlMode = 'advanced';

  // F-MOB-M1: per-scope sensitivity multiplier (1.0 hip / 0.8 reddot / 0.3 scoped).
  // game.ts updates this each frame based on current weapon scope state.
  private _scopeMultiplier = 1.0;

  // M1: Settings listener for cleanup
  private _settingsListener: ((s: any) => void) | null = null;

  constructor(useGyro = false) {
    this.useGyro = useGyro;
    this.mode = useGyro ? 'touch-gyro' : 'touch';
  }

  /** Initialize all touch sub-systems. */
  init(): void {
    // M1: Wire touch/gyro sensitivity from settings
    const settings = settingsStore.get();
    this.look = new TouchLookController(settings.touchSensitivity);
    this.joystick = new TouchJoystick();
    this.buttons = new TouchButtons();

    if (this.useGyro) {
      this.gyro = new GyroAimController(settings.gyroSensitivity);
      // MOB-L3: apply initial accelerometer flag from settings.
      if (typeof settings.gyroUseAccelerometer === 'boolean') {
        this.gyro.setUseAccelerometer(settings.gyroUseAccelerometer);
      }
      // F-MOB-H1: apply initial gyro deadzone from settings.
      if (typeof settings.gyroDeadZone === 'number') {
        this.gyro.setDeadZone(settings.gyroDeadZone);
      }
      // M2: Permission must be requested from a user gesture — see requestGyroPermission()
    }

    // M1: Read aim assist strength and control mode from initial settings
    this._aimAssistStrength = settings.aimAssistStrength;
    this._controlMode = settings.controlMode;

    // MOB-L2: Apply initial deadzone from settings (constructor reads it too,
    // but we ensure subsequent setting updates are honored).
    if (this.joystick && typeof settings.touchJoystickDeadzone === 'number') {
      this.joystick.setDeadzone(settings.touchJoystickDeadzone);
    }

    // M1: React to settings changes
    this._settingsListener = (s: ReturnType<typeof settingsStore.get>) => {
      if (this.look) this.look.sensitivity = s.touchSensitivity;
      if (this.gyro) this.gyro.sensitivity = s.gyroSensitivity;
      // M1: Keep aim assist strength and control mode in sync with settings
      this._aimAssistStrength = s.aimAssistStrength;
      // F-MOB-M3: re-apply layout when control mode changes.
      if (this._controlMode !== s.controlMode && this.buttons) {
        this.buttons.applyControlMode(s.controlMode);
      }
      this._controlMode = s.controlMode;
      // MOB-L2: keep joystick deadzone in sync
      if (this.joystick && typeof s.touchJoystickDeadzone === 'number') {
        this.joystick.setDeadzone(s.touchJoystickDeadzone);
      }
      // MOB-L3: keep accelerometer flag in sync
      if (this.gyro && typeof s.gyroUseAccelerometer === 'boolean') {
        this.gyro.setUseAccelerometer(s.gyroUseAccelerometer);
      }
      // F-MOB-H1: keep gyro deadzone in sync
      if (this.gyro && typeof s.gyroDeadZone === 'number') {
        this.gyro.setDeadZone(s.gyroDeadZone);
      }
      // F-MOB-H2: re-read look split-zone when settings change.
      this.look?.refreshZoneMinX();
    };
    settingsStore.onChange(this._settingsListener);

    // MOB-W4: Track vertical drag on the fire button while it is held.
    // Each frame the selector holds the absolute Y delta from the touch's
    // first contact, so the strength reflects the current drag distance
    // rather than the per-event delta. We use the absolute pattern so a
    // user can swipe up, then back down, and the displayed strength tracks.
    if (typeof document !== 'undefined') {
      this.fireBtnEl = document.getElementById('btn-fire');
    }
    if (this.fireBtnEl) {
      this._onFireTouchStart = (e: TouchEvent) => {
        for (let i = 0; i < e.changedTouches.length; i++) {
          const t = e.changedTouches[i];
          this._fireTouchYStart.set(t.identifier, t.clientY);
        }
        this.grenadeStrength.reset();
      };
      this._onFireTouchMove = (e: TouchEvent) => {
        for (let i = 0; i < e.changedTouches.length; i++) {
          const t = e.changedTouches[i];
          const startY = this._fireTouchYStart.get(t.identifier);
          if (startY === undefined) continue;
          this.grenadeStrength.reset();
          this.grenadeStrength.addDeltaY(t.clientY - startY);
        }
      };
      this._onFireTouchEnd = (e: TouchEvent) => {
        for (let i = 0; i < e.changedTouches.length; i++) {
          this._fireTouchYStart.delete(e.changedTouches[i].identifier);
        }
      };
      this.fireBtnEl.addEventListener('touchstart', this._onFireTouchStart, { passive: true });
      this.fireBtnEl.addEventListener('touchmove', this._onFireTouchMove, { passive: true });
      this.fireBtnEl.addEventListener('touchend', this._onFireTouchEnd, { passive: true });
      this.fireBtnEl.addEventListener('touchcancel', this._onFireTouchEnd, { passive: true });
    }

    // MOB-VIS: Touch overlays must NOT render on top of the main menu /
    // team-select screens. Start hidden; game.ts toggles via setVisible(true)
    // when the player transitions to an in-match state ('playing',
    // 'spectating', 'dead') and back to false on 'menu' / 'team_select'.
    this.joystick?.hide();
    this.buttons?.hide();
  }

  /**
   * MOB-VIS: Show or hide the touch overlays (joystick + action buttons).
   * Called by game.ts on state transitions so the on-screen controls only
   * appear in in-match states.
   */
  setVisible(visible: boolean): void {
    if (visible) {
      this.joystick?.show();
      this.buttons?.show();
    } else {
      this.joystick?.hide();
      this.buttons?.hide();
    }
  }

  /** Build an InputState snapshot from all touch sub-systems. */
  snapshot(): InputState {
    const move = this.joystick?.getMovement() ?? {
      forward: false,
      back: false,
      left: false,
      right: false,
      walk: false,
    };
    const btnState = this.buttons?.getState() ?? {
      fire: false,
      ads: false,
      reload: false,
      jump: false,
      crouch: false,
      weaponSwitch: false,
      ptt: false,
      scope: false,
      interact: false,
      buy: false,
      scoreboard: false,
      grenade: false,
      ping: false,
      chat: false,
      quickbuy1: false,
      quickbuy2: false,
      quickbuy3: false,
      inspect: false,
      drop: false,
      mapvote: false,
      radio: false,
    };

    // Look delta from touch drag
    const lookDelta = this.look?.getDelta() ?? { dx: 0, dy: 0 };
    this.look?.reset();

    // Optionally add gyro delta
    let gyroDelta = { dx: 0, dy: 0 };
    if (this.gyro) {
      gyroDelta = this.gyro.getDelta();
    }

    // H1: Detect weapon switch rising edge (false→true) and fire callback
    if (btnState.weaponSwitch && !this.prevWeaponSwitch && this.weaponSwitchCallback) {
      this.weaponSwitchCallback();
    }
    this.prevWeaponSwitch = btnState.weaponSwitch;

    // C6: Mobile PTT edges drive voiceChat transmit state (analog of CapsLock PTT on desktop).
    // F-MOB-M4: rising edge runs the preflight (mic permission + AudioContext
    // unlock) before transmitting. Falling edge always stops — no preflight
    // needed.
    if (btnState.ptt && !this.prevPTT) {
      // BUG-MT-9: synchronously mark the transmit state as 'warming' so any
      // VAD analyser ticks during the preflight gap suppress speaking=true
      // events. Mirrors the keyboard PTT path in voice-input.ts so the LIVE
      // indicator doesn't flash before the mic actually goes live.
      voiceChat.setTransmitState?.('warming');
      // MT-30: flip the visual PTT-transmitting class on the btn-ptt
      // element immediately on the rising edge so the player gets instant
      // feedback that their press registered. The class is removed on the
      // falling edge below, or rolled back on preflight failure.
      this.buttons?.setPTTTransmitting(true);
      void voiceChat.preflightPTT().then((res) => {
        // BUG-MT-18: caller may have released by the time the preflight
        // resolves. `this.prevPTT` reflects the *previous* snapshot (which
        // was just set to true immediately after this branch ran) — not
        // whether PTT is still held NOW. Read the live button state instead.
        const stillHeld = this.buttons?.getState().ptt ?? false;
        if (res === 'ok' && stillHeld) {
          voiceChat.startTransmitting();
        } else {
          // Preflight failed, or user released mid-preflight. Roll back the
          // warming state so VAD/LIVE indicator returns to idle.
          voiceChat.setTransmitState?.('idle');
          // MT-30: also roll back the visual feedback on preflight failure
          // so the button doesn't stay highlighted when no mic is going out.
          this.buttons?.setPTTTransmitting(false);
        }
      });
    } else if (!btnState.ptt && this.prevPTT) {
      voiceChat.stopTransmitting();
      // MT-30: clear the visual feedback on falling edge.
      this.buttons?.setPTTTransmitting(false);
    }
    this.prevPTT = btnState.ptt;

    // MOB-C2: Detect scope rising edge (false→true) and fire callback (analog of toggleScope).
    if (btnState.scope && !this.prevScope && this.scopeCallback) {
      this.scopeCallback();
    }
    this.prevScope = btnState.scope;

    // MOB-C3: Detect interact rising edge (false→true) and fire callback (analog of desktop E).
    // BUG-MT-1 / BUG-W-10: also latch the rising edge so the binary input frame
    // gets ACT_USE set. game.ts pulls this via popInteract() at encode-time.
    if (btnState.interact && !this.prevInteract) {
      this.pendingInteract = true;
      if (this.interactCallback) this.interactCallback();
    }
    this.prevInteract = btnState.interact;

    // F-MOB-H3: Buy menu / scoreboard / grenade-cycle / ping wheel rising edges.
    if (btnState.buy && !this.prevBuy && this.buyCallback) this.buyCallback();
    this.prevBuy = btnState.buy;
    if (btnState.scoreboard && !this.prevScoreboard && this.scoreboardCallback) this.scoreboardCallback();
    this.prevScoreboard = btnState.scoreboard;
    if (btnState.grenade && !this.prevGrenade && this.grenadeCycleCallback) this.grenadeCycleCallback();
    this.prevGrenade = btnState.grenade;
    if (btnState.ping && !this.prevPing && this.pingCallback) this.pingCallback();
    this.prevPing = btnState.ping;
    // F-MOB-H3 (chat): chat-button rising edge opens the ChatComposer overlay.
    if (btnState.chat && !this.prevChat && this.chatCallback) this.chatCallback();
    this.prevChat = btnState.chat;

    // MOB-W6: rising edges on the three quick-buy slots fire the host's
    // single quick-buy callback with the matching slot index. The host
    // routes through QuickBuyController.triggerBuy(slot).
    const qbStates = [btnState.quickbuy1, btnState.quickbuy2, btnState.quickbuy3];
    for (let i = 0; i < 3; i++) {
      if (qbStates[i] && !this.prevQuickBuy[i] && this.quickBuyCallback) {
        this.quickBuyCallback(i as 0 | 1 | 2);
      }
      this.prevQuickBuy[i] = qbStates[i];
    }

    // F-GP-006: Latch inspect / drop rising edges so popInspect()/popDrop()
    // can hand the press to game.ts at encode-time. We don't fire callbacks
    // here — the encode pipeline reads the latch directly.
    if (btnState.inspect && !this.prevInspect) this.pendingInspect = true;
    this.prevInspect = btnState.inspect;
    if (btnState.drop && !this.prevDrop) this.pendingDrop = true;
    this.prevDrop = btnState.drop;

    // MOB-W4: Detect fire FALLING edge — release-to-throw for grenades.
    // BUG-MT-21: gate the callback at the source via the optional
    // grenadeFireGate predicate. If the gate returns false, swallow the
    // release (no callback, but still reset the strength selector so a
    // subsequent press starts at neutral). game.ts still gates by
    // `selectedGrenade`, but the predicate adds a defence-in-depth check
    // at the source so a stray release on a non-grenade weapon never
    // reaches the host.
    if (!btnState.fire && this.prevFire && this.grenadeFireCallback) {
      const allow = this.grenadeFireGate ? this.grenadeFireGate() : true;
      if (allow) {
        const strength = this.grenadeStrength.getStrength();
        this.grenadeFireCallback(strength);
      }
      this.grenadeStrength.reset();
    }
    this.prevFire = btnState.fire;

    // F-MOB-M1 / MOB-W8: apply per-scope sensitivity multiplier to look + gyro
    // delta so the same drag distance produces less yaw/pitch when scoped.
    const m = this._scopeMultiplier;
    return {
      forward: move.forward,
      back: move.back,
      left: move.left,
      right: move.right,
      jump: btnState.jump,
      crouch: btnState.crouch,
      walk: move.walk,
      fire: btnState.fire,
      reload: btnState.reload,
      ads: btnState.ads,
      mouseDx: (lookDelta.dx + gyroDelta.dx) * m,
      mouseDy: (lookDelta.dy + gyroDelta.dy) * m,
    };
  }

  /** F-MOB-M1: set the per-scope sensitivity multiplier applied to all look
   *  + gyro deltas in subsequent snapshots. game.ts feeds the result of
   *  scopeSensitivity[currentScopeKey] each frame. */
  setScopeMultiplier(value: number): void {
    if (!Number.isFinite(value)) return;
    this._scopeMultiplier = Math.max(0.05, Math.min(3, value));
  }

  /** F-MOB-M1: read-only accessor for the current scope multiplier. */
  getScopeMultiplier(): number {
    return this._scopeMultiplier;
  }

  /** F-GP-006: consume the latched inspect rising-edge for the current encode tick.
   *  Returns true exactly once per press; subsequent calls return false until the
   *  next press. game.ts feeds this into encodeActionsFromState(state, inspect, ..). */
  popInspect(): boolean {
    if (!this.pendingInspect) return false;
    this.pendingInspect = false;
    return true;
  }

  /** F-GP-006: consume the latched drop rising-edge for the current encode tick.
   *  Returns true exactly once per press; subsequent calls return false until the
   *  next press. game.ts feeds this into encodeActionsFromState(state, _, _, drop). */
  popDrop(): boolean {
    if (!this.pendingDrop) return false;
    this.pendingDrop = false;
    return true;
  }

  /** BUG-MT-1 / BUG-W-10: consume the latched USE (interact) rising-edge for the
   *  current encode tick. Returns true exactly once per press; subsequent calls
   *  return false until the next press. game.ts feeds this as the `use` arg
   *  into encodeActionsFromState so ACT_USE is set on the binary input frame
   *  (parity with desktop reading the E keybind). */
  popInteract(): boolean {
    if (!this.pendingInteract) return false;
    this.pendingInteract = false;
    return true;
  }

  /** H1: Register a callback for the weapon switch touch button. Fires once per press. */
  onWeaponSwitch(cb: () => void): void {
    this.weaponSwitchCallback = cb;
  }

  /** MOB-C2: Register a callback for the scope toggle touch button. Fires once per press. */
  onScope(cb: () => void): void {
    this.scopeCallback = cb;
  }

  /** MOB-C3: Register a callback for the interact (plant/defuse/use) touch button. Fires once per press. */
  onInteract(cb: () => void): void {
    this.interactCallback = cb;
  }

  /** F-MOB-H3: Register callbacks for the new mobile buttons. Each fires once per press. */
  onBuy(cb: () => void): void { this.buyCallback = cb; }
  onScoreboard(cb: () => void): void { this.scoreboardCallback = cb; }
  onGrenadeCycle(cb: () => void): void { this.grenadeCycleCallback = cb; }
  onPing(cb: () => void): void { this.pingCallback = cb; }
  /** F-MOB-H3 (chat): Register a callback for the chat-button rising edge.
   *  Fires once per press. The callback is responsible for opening the
   *  ChatComposer overlay. */
  onChat(cb: () => void): void { this.chatCallback = cb; }

  /** MOB-W6: Register a single callback for the three quick-buy buttons.
   *  Fires once per press, with the slot index (0-2). The callback
   *  routes to QuickBuyController.triggerBuy(slot). */
  onQuickBuy(cb: (slot: 0 | 1 | 2) => void): void { this.quickBuyCallback = cb; }

  /** BUG-MT-7: forward the mobile map-vote button callback to TouchButtons. */
  setOnMapVote(cb: (() => void) | null): void {
    this.buttons?.setOnMapVote(cb);
  }

  /** BUG-PARITY-H2: forward the mobile radio command-wheel button callback to
   *  TouchButtons. Desktop has Z/X/C radio submenus; the mobile RAD button
   *  opens the same wheel via this rising-edge dispatch. */
  setOnRadio(cb: (() => void) | null): void {
    this.buttons?.setOnRadio(cb);
  }

  /** F-MOB-H3 (chat): expose the underlying TouchLookController so the chat
   *  composer can pause it while the soft keyboard is up. */
  setTouchLookEnabled(enabled: boolean): void {
    this.look?.setEnabled(enabled);
  }

  /**
   * MOB-W4: Register a callback for grenade throw (fire button release on mobile).
   * Fires on the falling edge of the fire button with the current swipe-derived
   * strength (0=overhand, 1=medium, 2=underhand). The callback is invoked on
   * every fire release; the consumer is responsible for gating by `selectedGrenade`.
   */
  onGrenadeFire(cb: (strength: GrenadeStrength) => void): void {
    this.grenadeFireCallback = cb;
  }

  /**
   * BUG-MT-21: Install (or clear with null) a predicate that decides whether
   * the grenadeFireCallback should fire on the next fire-release. The
   * predicate is evaluated synchronously inside snapshot() at the falling
   * edge of fire. Returning false swallows the callback (the strength
   * selector still resets) so non-grenade fire releases never reach the
   * host. Pass null to remove the gate (callback fires unconditionally).
   */
  setGrenadeFireGate(predicate: (() => boolean) | null): void {
    this.grenadeFireGate = predicate;
  }

  /** MOB-W4: Read-only accessor for the in-progress strength (HUD preview). */
  getGrenadeStrength(): GrenadeStrength {
    return this.grenadeStrength.getStrength();
  }

  /** M1: Current aim assist strength (0-1). Read from settings, updated on change. */
  get aimAssistStrength(): number {
    return this._aimAssistStrength;
  }

  /** M1: Current control mode. Read from settings, updated on change. */
  get controlMode(): ControlMode {
    return this._controlMode;
  }

  /** Get the gyro controller for permission requests. Null if gyro not enabled. */
  getGyro(): GyroAimController | null {
    return this.gyro;
  }

  /** Get the touch buttons container (used by HudLayoutEditor). Null before init(). */
  getButtons(): TouchButtons | null {
    return this.buttons;
  }

  /**
   * M2: Request gyroscope permission (iOS 13+ requires user gesture).
   * Call this from a UI button click handler.
   * Returns true if permission granted or not needed.
   */
  async requestGyroPermission(): Promise<boolean> {
    if (!this.gyro) return false;
    return this.gyro.requestPermission();
  }

  /** Tear down all sub-systems. */
  dispose(): void {
    // C6: If PTT was held when we tear down, ensure we release the voice transmit.
    if (this.prevPTT) {
      voiceChat.stopTransmitting();
      // MT-30: also clear the visual feedback on teardown so a stale
      // .ptt-transmitting class isn't left on the button.
      this.buttons?.setPTTTransmitting(false);
      this.prevPTT = false;
    }

    // MOB-W4: Detach fire-button drag listeners.
    if (this.fireBtnEl) {
      if (this._onFireTouchStart) this.fireBtnEl.removeEventListener('touchstart', this._onFireTouchStart);
      if (this._onFireTouchMove)  this.fireBtnEl.removeEventListener('touchmove',  this._onFireTouchMove);
      if (this._onFireTouchEnd) {
        this.fireBtnEl.removeEventListener('touchend',    this._onFireTouchEnd);
        this.fireBtnEl.removeEventListener('touchcancel', this._onFireTouchEnd);
      }
    }
    this._fireTouchYStart.clear();
    this._onFireTouchStart = null;
    this._onFireTouchMove = null;
    this._onFireTouchEnd = null;
    this.fireBtnEl = null;

    this.look?.dispose();
    this.joystick?.dispose();
    this.buttons?.dispose();
    this.gyro?.dispose();
    if (this._settingsListener) {
      settingsStore.offChange(this._settingsListener);
    }

    this.look = null;
    this.joystick = null;
    this.buttons = null;
    this.gyro = null;
    this.weaponSwitchCallback = null;
    this.scopeCallback = null;
    this.interactCallback = null;
    this.grenadeFireCallback = null;
    this.grenadeFireGate = null;
    this.buyCallback = null;
    this.scoreboardCallback = null;
    this.grenadeCycleCallback = null;
    this.pingCallback = null;
    this.chatCallback = null;
    this.quickBuyCallback = null;
    this._settingsListener = null;
  }
}
